import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import { AccountsEndpoints, type AccountListQuery } from './accountsEndpoints';
import {
    mapAccountDetail,
    mapAccountList,
    type AccountListWire,
    type AccountWire,
} from './accountsMapping';
import type { IAccountsService } from './IAccountsService';
import type { AccountCreateRequest, AccountUpdateRequest } from './requests';
import type {
    AccountWriteResponse,
    GetAccountListResponse,
    GetAccountResponse,
} from './responses';

type AccountsServiceOptions = {
    baseUrl: string;
};

/** A body this client could not map is contract drift, not a value to guess at. */
const UNMAPPABLE_ACCOUNT: ApiResponse<never> = {
    success: false,
    error: {
        isAxiosError: false,
        status: 500,
        name: '500',
        message: 'The server returned an account this client cannot read.',
        code: 'UNMAPPABLE_ACCOUNT',
    },
};

/**
 * The live ledger-accounts client (bos-api, BOS-91).
 *
 * Every method maps at the boundary via `accountsMapping`, so nothing downstream
 * sees a wire shape — and the field names the server uses are confined to that
 * one file. `makeApiRequest` turns a 4xx into a `{ success: false }` envelope
 * carrying the machine-readable `code` (`CODE_DUPLICATE`, `STALE_UPDATE`, …), so
 * the form branches on the code and never on the message text.
 */
export class AccountsService implements IAccountsService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: AccountsServiceOptions) {
        this.baseUrl = baseUrl;
    }

    private get client() {
        return getAxiosClient({ baseURL: this.baseUrl });
    }

    /**
     * The chart, with balances on the rows.
     *
     * The server orders by name (case-insensitive) then code. The screen does not
     * use that order: a chart of accounts is navigated by code, and names are
     * localized, so a name order silently reshuffles the whole tree when the user
     * switches language. `buildAccountTree` re-sorts siblings by code at every
     * level, so the response order here is not load-bearing.
     */
    public async getAccountList(
        query: AccountListQuery = {},
    ): Promise<ApiResponse<GetAccountListResponse>> {
        const result = await makeApiRequest<AccountListWire>(
            this.client.get(AccountsEndpoints.list(query)),
        );
        if (!result.success) {
            return result;
        }
        const { items, bootstrapRequired, dropped } = mapAccountList(
            result.response,
        );
        if (dropped.length > 0) {
            // Loud, but not fatal: a chart missing one unreadable row is still
            // usable, and silence here would look like the account was deleted.
            console.warn(
                `[accounts] dropped ${dropped.length} unreadable account row(s): ${dropped.join(', ')}`,
            );
        }
        return { success: true, response: { items, bootstrapRequired } };
    }

    public async getAccount(
        id: string,
    ): Promise<ApiResponse<GetAccountResponse>> {
        const result = await makeApiRequest<AccountWire>(
            this.client.get(AccountsEndpoints.byId(id)),
        );
        if (!result.success) {
            return result;
        }
        const detail = mapAccountDetail(result.response);
        return detail
            ? { success: true, response: detail }
            : UNMAPPABLE_ACCOUNT;
    }

    /**
     * Create. Idempotent on the client-supplied id, and **both `200` and `201`
     * are success** — `201` on the first create, `200` on a replay of the same
     * id. `makeApiRequest` already treats both as success, so a retried submit
     * cannot duplicate the account.
     */
    public async createAccount(
        body: AccountCreateRequest,
    ): Promise<ApiResponse<AccountWriteResponse>> {
        const result = await makeApiRequest<AccountWire>(
            this.client.post(AccountsEndpoints.create(), body),
        );
        return toWriteResponse(result);
    }

    /**
     * Patch-style update. `body.etag` is the token from the last read; a
     * mismatch comes back as `409 STALE_UPDATE` and the form recovers from it
     * without discarding the user's input.
     */
    public async updateAccount(
        id: string,
        body: AccountUpdateRequest,
    ): Promise<ApiResponse<AccountWriteResponse>> {
        const result = await makeApiRequest<AccountWire>(
            this.client.put(AccountsEndpoints.update(id), body),
        );
        return toWriteResponse(result);
    }

    /**
     * Archive or reactivate.
     *
     * These answer `200` with the updated account (they used to be `204`), so the
     * response carries a fresh etag that the caller must adopt — a subsequent
     * edit made with the pre-archive token would 409.
     *
     * No etag is sent: the contract states the round-trip for `PUT` only, and
     * inventing a body for a POST the server may validate strictly would trade a
     * real capability for a guess. (Reported as an open item.)
     */
    public async setAccountArchived(
        id: string,
        archived: boolean,
    ): Promise<ApiResponse<AccountWriteResponse>> {
        const url = archived
            ? AccountsEndpoints.archive(id)
            : AccountsEndpoints.reactivate(id);
        const result = await makeApiRequest<AccountWire>(this.client.post(url));
        return toWriteResponse(result);
    }
}

const toWriteResponse = (
    result: ApiResponse<AccountWire>,
): ApiResponse<AccountWriteResponse> => {
    if (!result.success) {
        return result;
    }
    const detail = mapAccountDetail(result.response);
    return detail ? { success: true, response: detail } : UNMAPPABLE_ACCOUNT;
};
